// Types for Point of Sale (POS) interface

export const Currency = {
    USD: 'USD',
    CAD: 'CAD',
    NZD: 'NZD',
    AUD: 'AUD',
    ZAR: 'ZAR',
    JPY: 'JPY',
    CNY: 'CNY',
    GBP: 'GBP',
    EUR: 'EUR',
} as const;

export type Currency = typeof Currency[keyof typeof Currency];

export const VatMode = {
    STANDARD: 'STANDARD',
    EXEMPT: 'EXEMPT',
    ZERO_RATED: 'ZERO_RATED',
    TOURIST: 'TOURIST',
} as const;

export type VatMode = typeof VatMode[keyof typeof VatMode];

export const PaymentMethod = {
    CASH: 'CASH',
    CARD: 'CARD', // Maps to Speedpoint
    EFT: 'EFT',
} as const;

export type PaymentMethod = typeof PaymentMethod[keyof typeof PaymentMethod];

export const DiscountType = {
    PERCENTAGE: 'PERCENTAGE',
    FIXED_AMOUNT: 'FIXED_AMOUNT',
} as const;

export type DiscountType = typeof DiscountType[keyof typeof DiscountType];

export interface SaleLineItem {
    id?: number;
    productId?: number;
    itemDescription: string;
    quantity: number;
    unitPrice: string;
    lineTotal: string;
    unitCost?: string;
    sku?: string;
}

export interface Sale {
    id?: number;
    customerId?: number;
    customerName?: string;
    customerIdNumber?: string;
    customerPassportNumber?: string;
    currency: Currency;
    exchangeRate: number;
    subtotal: number;
    taxAmount: number;
    total: number;
    vatMode?: VatMode;
    vatRate?: number;
    vatExemptionReason?: string;
    discountType?: DiscountType;
    discountAmount?: number;
    discountApplied?: number;
    lineItems: SaleLineItem[];
    notes?: string;
    paymentMethod?: string;
    salespersonName?: string;
    storeLocation?: string;
    grossProfit?: number;
    createdAt?: string;
    updatedAt?: string;
    /** Server-set on the backdated-sale flow only; ignored if submitted on a normal sale. */
    backdated?: boolean;
    /** Server-set on the backdated-sale flow only; ignored if submitted on a normal sale. */
    backdateReason?: string;
}

export interface CartItem {
    productId?: number;
    itemDescription: string;
    quantity: number;
    unitPrice: number;
    unitCost?: number;
    sku?: string;
    image?: string;
}

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
    [Currency.USD]: '$',
    [Currency.CAD]: 'C$',
    [Currency.NZD]: 'NZ$',
    [Currency.AUD]: 'A$',
    [Currency.ZAR]: 'R',
    [Currency.JPY]: '¥',
    [Currency.CNY]: '¥',
    [Currency.GBP]: '£',
    [Currency.EUR]: '€',
};

export const CURRENCY_DISPLAY_NAMES: Record<Currency, string> = {
    [Currency.USD]: 'US Dollar',
    [Currency.CAD]: 'Canadian Dollar',
    [Currency.NZD]: 'New Zealand Dollar',
    [Currency.AUD]: 'Australian Dollar',
    [Currency.ZAR]: 'South African Rand',
    [Currency.JPY]: 'Japanese Yen',
    [Currency.CNY]: 'Chinese Yuan',
    [Currency.GBP]: 'British Pound Sterling',
    [Currency.EUR]: 'Euro',
};

export interface Product {
    id: number;
    name: string;
    description?: string;
    sku: string;
    barcode?: string;
    unitPrice: number;
    unitCost: number;
    stockQuantity: number;
    image?: string;
    active: boolean;
    consignment?: boolean;
    supplierId?: number;
    supplierName?: string;
    categoryId?: number;
    categoryName?: string;
    stockGroupId?: number;
    stockGroupName?: string;
    stockAdjustmentReason?: string;
    /** ID of the label template explicitly assigned to this product. */
    labelTemplateId?: number | null;
    /** Resolved template ID: product-level override → category default → null. */
    resolvedLabelTemplateId?: number | null;
}
